import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchEntityListAction } from "../../actions/entityListActions";
import { updateEntityAction } from "../../actions/updateEntityAction";

// helper: detect if string looks like an email
const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// Entity category options
export const entityCategoryOptions = [
    { value: '', label: 'All Entity Categories' },
    { value: 'client', label: 'Client' },
    { value: 'vendor', label: 'Vendor' },
];

// Document status options
export const documentStatusOptions = [
    { value: '', label: 'All Documents Status' },
    // { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending' },
    { value: 'Done', label: 'Done' },
];
/**
 * Table column configuration for entity list
 */
export const entityListColumns = [
    {
        header: "Entity Name",
        accessor: "entityName",
        render: (row) => row.entity?.entityName || "-"
    },
    {
        header: "Contact Person",
        accessor: "contactPerson",
        render: (row) => row.entity?.contactPerson || "-"
    },
    {
        header: "Email",
        accessor: "emailAddress",
        render: (row) => row.entity?.emailAddress || "-"
    },
    {
        header: "Entity Category",
        accessor: "entityCategory",
        render: (row) => row.entity?.entityCategory || "-"
    },
    {
        header: "LOB Type",
        accessor: "lobType",
        render: (row) => row.entity?.lobType || "-"
    },
    {
        header: "Documents Status",
        accessor: "documentsStatus",
        render: (row) => {
            const documents = row.documents || [];
            const totalDocs = documents.length;
            const uploadedDocs = documents.filter(doc => doc.isUploaded).length;

            const status = totalDocs === 0 ? "No Documents" :
                uploadedDocs === totalDocs ? "Uploaded" :
                    uploadedDocs > 0 ? "Partial" : "Pending";

            return (
                <span className={`status-badge ${status.toLowerCase()}`}>
                    {status}
                </span>
            );
        }
    }
];

/**
 * Custom hook for managing entity list state and operations with server-side pagination
 */
export const useEntityList = () => {
    const location = useLocation();
    const initialCategory = new URLSearchParams(location.search).get('entityCategory') || '';
    const initialDocStatus = new URLSearchParams(location.search).get('documentStatus') || '';

    const [currentPage, setCurrentPage] = useState(1);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [entities, setEntities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize: 10,
        hasNextPage: false,
        hasPreviousPage: false
    });

    const [searchQuery, setSearchQuery] = useState('');
    const [entityCategory, setEntityCategory] = useState(initialCategory);
    const [documentStatus, setDocumentStatus] = useState(initialDocStatus);

    const itemsPerPage = 10;
    const debounceTimer = useRef(null);
    const isFirstRender = useRef(true);
    const prevSearchRef = useRef('');
    const isLoadingRef = useRef(false); // guard against concurrent calls

    // Build filters from current state
    const buildFilters = useCallback((query, category, status) => {
        const filters = {};
        if (query) {
            if (isEmail(query)) {
                filters.emailAddress = query.trim();
            } else {
                filters.entityName = query.trim();
            }
        }
        if (category) filters.entityCategory = category;
        if (status) filters.status = status;
        return filters;
    }, []);

    const loadEntityList = useCallback(async (page = 1, query = '', category = '', status = '') => {
        if (isLoadingRef.current) {
            console.log('[EntityList] Already loading — skipped duplicate call');
            return;
        }
        isLoadingRef.current = true;
        setIsLoading(true);
        try {
            const filters = buildFilters(query, category, status);
            console.log(`[EntityList] Fetching page=${page}`, filters);
            const response = await fetchEntityListAction(page, itemsPerPage, filters);

            if (response.success) {
                const data = response.data || [];
                const serverPagination = response.pagination || {};
                const correctedPagination = {
                    ...serverPagination,
                    totalItems: serverPagination.totalItems || data.length,
                    totalPages: serverPagination.totalPages || Math.ceil((serverPagination.totalItems || data.length) / itemsPerPage),
                    hasNextPage: serverPagination.hasNextPage !== undefined ? serverPagination.hasNextPage : (page < (serverPagination.totalPages || 1)),
                    hasPreviousPage: serverPagination.hasPreviousPage !== undefined ? serverPagination.hasPreviousPage : (page > 1)
                };
                setEntities(data);
                setPagination(correctedPagination);
            } else {
                toast.error(response.message || "Failed to load entity list");
                setEntities([]);
                setPagination({ currentPage: 1, totalPages: 1, totalItems: 0, pageSize: itemsPerPage, hasNextPage: false, hasPreviousPage: false });
            }
        } catch (error) {
            toast.error("Failed to load entity list");
            setEntities([]);
            setPagination({ currentPage: 1, totalPages: 1, totalItems: 0, pageSize: itemsPerPage, hasNextPage: false, hasPreviousPage: false });
        } finally {
            setIsLoading(false);
            isLoadingRef.current = false;
        }
    }, [buildFilters]);

    // Single effect handles initial load + all filter/page changes.
    // Search is debounced 400ms; dropdown changes and page changes are immediate.
    useEffect(() => {
        let cancelled = false;

        const isSearchChange = !isFirstRender.current &&
            prevSearchRef.current !== searchQuery;
        const delay = isSearchChange ? 400 : 0;
        prevSearchRef.current = searchQuery;
        isFirstRender.current = false;

        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            if (!cancelled) {
                loadEntityList(currentPage, searchQuery, entityCategory, documentStatus);
            }
        }, delay);

        return () => {
            cancelled = true;
            clearTimeout(debounceTimer.current);
        };
    }, [currentPage, searchQuery, entityCategory, documentStatus]); // eslint-disable-line react-hooks/exhaustive-deps

    // Page change
    const handlePageChange = (page) => {
        if (page !== currentPage && page >= 1) {
            setCurrentPage(page);
            // effect will fire automatically via currentPage dep
        }
    };

    // Filter setters — reset page to 1 so effect fires with correct page
    const handleSearchChange = (value) => {
        setCurrentPage(1);
        setSearchQuery(value);
    };

    const handleCategoryChange = (value) => {
        setCurrentPage(1);
        setEntityCategory(value);
    };

    const handleStatusChange = (value) => {
        setCurrentPage(1);
        setDocumentStatus(value);
    };

    const handleEdit = (entityRow) => {
        setSelectedClient(entityRow);
        setIsEditModalOpen(true);
    };

    const handleSaveClient = async (updatedData) => {
        try {
            const response = await updateEntityAction(updatedData.id, updatedData);
            if (response.success) {
                toast.success("Entity updated successfully!");
                await loadEntityList(currentPage);
                return response;
            } else {
                toast.error(response.message || "Failed to update entity");
                throw new Error(response.message);
            }
        } catch (error) {
            // Only toast if not already toasted above
            if (!error.message || error.message === 'Failed to update entity') {
                toast.error("Failed to update entity");
            }
            throw error;
        }
    };

    const handleCloseModal = () => {
        setIsEditModalOpen(false);
        setSelectedClient(null);
    };

    const handleDelete = (entityRow) => {
        const entityName = entityRow.entity?.entityName || "this entity";
        if (window.confirm(`Are you sure you want to delete ${entityName}?`)) {
            toast.info("Delete functionality coming soon");
        }
    };

    return {
        currentPage,
        setCurrentPage: handlePageChange,
        isEditModalOpen,
        selectedClient,
        entities,
        isLoading,
        itemsPerPage,
        pagination,
        handleEdit,
        handleSaveClient,
        handleCloseModal,
        handleDelete,
        totalPages: pagination.totalPages,
        currentEntities: entities,
        searchQuery,
        setSearchQuery: handleSearchChange,
        entityCategory,
        setEntityCategory: handleCategoryChange,
        documentStatus,
        setDocumentStatus: handleStatusChange
    };
};